import { isResource } from '@medplum/core'
import type {
  CodeableConcept,
  Coding,
  Composition,
  CompositionEvent,
  CompositionSection,
  Library,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeableConceptHasSystem,
  systemFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { ASSESSMENT_TYPE, NOTE_LABEL_TAG_SYSTEM } from '#src/codeSystems.ts'
import { Extension, Tag } from '#src/decorators.ts'
import { htmlEquivalent } from '#src/htmlEquivalent.ts'
import { invariant } from '#src/invariant.ts'
import { ASSESSMENT_TYPES } from '#src/models/assessments/constants.ts'
import type { AssessmentTemplate } from '#src/models/assessmentTemplate.ts'
import { isDroppableEmptySection } from '#src/models/compositionLibraryEncoded.ts'
import { ExtendedComposition } from '#src/models/compositions/composition.ts'
import { CompositionCategory } from '#src/models/compositions/constants.ts'
import { withValidatedToolEntries } from '#src/models/compositions/validatedToolEmbeds.ts'
import {
  type NoteLabel,
  noteLabelTagCodings,
  readNoteLabelReferences,
} from '#src/models/noteLabel.ts'
import {
  andQueryFilters,
  assessmentKindCategoryFilter,
  isAssessmentVisitNoteCategoryToken,
  mergeVisitNoteCategoryTokens,
  noteLabelCategoryFilter,
  readVisitNoteCategoryTokens,
  resolveAssessmentKindToken,
  resolveDisciplineToken,
  visitNoteCategoryByToken,
} from '#src/models/visitNoteCategory.ts'
import { dateRangeFilter, type QueryTypes } from '#src/search.ts'
import { generateUUID, isOfType, now } from '#src/utils.ts'

export interface AssessmentSearchQuery {
  practitioner?: Reference<Practitioner>
  subject?: Reference<Patient>
  ids?: string[]
  kinds?: string[]
  disciplines?: string[]
  dateFrom?: string
  dateTo?: string
  signed?: boolean
}

export class Assessment extends ExtendedComposition {
  constructor(
    composition: Omit<Composition, 'resourceType' | 'date' | 'status'> &
      Partial<Pick<Composition, 'date' | 'status'>>
  ) {
    super({
      status: 'preliminary',
      date: now(),
      category: [CompositionCategory.Assessment],
      ...composition,
    })
  }

  static buildFromTemplate(
    practitioner: Reference<Practitioner>,
    participant: Reference<Patient>,
    template: AssessmentTemplate
  ): Assessment {
    const assessment = new Assessment({
      title: template.title,
      type: ASSESSMENT_TYPES.INITIAL,
      author: [practitioner],
    })
    assessment.subject = participant
    assessment.template = template.reference
    assessment.startsAt = now()
    assessment.sections = template.sections
      .filter((section) => !isDroppableEmptySection(section))
      .map((section) => ({
        ...section,
        id: generateUUID(),
      }))
    return assessment
  }

  static isInstance(value: unknown): value is Composition {
    return (
      isResource(value) &&
      isOfType(value, 'Composition') &&
      codeableConceptHasSystem(value.type, ASSESSMENT_TYPE)
    )
  }

  static assessmentSearchScopeFilters({
    practitioner,
    subject,
    ids,
    kinds,
    disciplines,
    dateFrom,
    dateTo,
    signed,
  }: AssessmentSearchQuery = {}): QueryTypes {
    const query: QueryTypes = {}
    if (practitioner) {
      query.author = practitioner.reference
    }
    if (subject) {
      query.subject = subject.reference
    }
    if (ids?.length) {
      query._id = ids.join(',')
    }
    Object.assign(
      query,
      andQueryFilters(
        noteLabelCategoryFilter((kinds ?? []).map(resolveAssessmentKindToken)),
        noteLabelCategoryFilter(
          (disciplines ?? []).map(resolveDisciplineToken)
        ),
        dateRangeFilter('period', { from: dateFrom, to: dateTo })
      )
    )
    if (signed !== undefined) {
      query['attester:missing'] = signed ? false : true
    }
    return query
  }

  static searchQuery(params: AssessmentSearchQuery = {}): QueryTypes {
    return {
      ...Assessment.assessmentSearchScopeFilters(params),
      _sort: '-_lastUpdated',
      category: CompositionCategory.Assessment,
      'status:not': 'entered-in-error',
    }
  }

  static assessmentsTabSearchQuery(
    params: AssessmentSearchQuery = {}
  ): QueryTypes {
    const scope = Assessment.assessmentSearchScopeFilters(params)
    const { _filter, ...scopeWithoutFilter } = scope
    const scopeFilter = typeof _filter === 'string' ? { _filter } : {}
    return {
      ...scopeWithoutFilter,
      ...andQueryFilters(assessmentKindCategoryFilter(), scopeFilter),
      _sort: '-period',
      category: CompositionCategory.Assessment,
      'status:not': 'entered-in-error',
    }
  }

  get title(): string {
    return this.resource.title
  }

  set title(title) {
    this.resource.title = title
  }

  @Extension({
    url: 'https://www.seenhealth.org/fhir/StructureDefinition/assessment-template',
    kind: 'valueReference',
  })
  template?: Reference<Library> | undefined

  get subject(): Reference<Patient> {
    invariant(
      isOfType(this.resource.subject, 'Patient'),
      'Assessment subject must be a Patient reference'
    )
    return this.resource.subject
  }

  set subject(subject: Reference<Patient>) {
    this.resource.subject = subject
  }

  get author(): Reference<Practitioner> {
    invariant(
      isOfType(this.resource.author[0], 'Practitioner'),
      'Assessment author must be a Practitioner reference'
    )
    return this.resource.author[0]
  }

  set author(author: Reference<Practitioner>) {
    this.resource.author = [author]
  }

  get date(): string {
    return this.resource.date
  }

  set date(date) {
    this.resource.date = date
  }

  get events(): CompositionEvent[] {
    return this.resource.event ?? []
  }

  get type(): CodeableConcept {
    // TODO: remove this once we have migrated to the new assessment type
    // invariant(this.resource.type.coding?.[0].system === ASSESSMENT_TYPE)
    return this.resource.type
  }

  set type(type: CodeableConcept) {
    invariant(
      systemFromCodeableConcept(type) === ASSESSMENT_TYPE,
      'Assessment type coding system must match ASSESSMENT_TYPE'
    )
    this.resource.type = type
  }

  get category(): CodeableConcept[] {
    return this.resource.category ?? []
  }

  set category(category: CodeableConcept[]) {
    this.resource.category = category
  }

  @Tag({
    system: NOTE_LABEL_TAG_SYSTEM,
    allowMultiple: true,
  })
  _labels?: Coding[]

  /**
   * Note labels on this assessment, as `NoteLabel` reference strings. Stored in
   * `meta.tag` under `NOTE_LABEL_TAG_SYSTEM` — the SAME vocabulary, predicate,
   * and storage as `CompositionNote.labels`, so a label means the same thing on
   * a note and an assessment.
   */
  get labels(): NoteLabel['referenceString'][] {
    return readNoteLabelReferences(this._labels)
  }

  set labels(labels: NoteLabel['referenceString'][] | undefined) {
    this._labels = noteLabelTagCodings(labels)
  }

  /**
   * Visit-note categories selected on this assessment, as `system|code` tokens
   * from the SAME `VISIT_NOTE_CATEGORIES` registry the notes use, stored as
   * additional `Composition.category[]` codings (token-searchable). The merge
   * preserves the `CompositionCategory.Assessment`/`Incident` bucket coding that
   * `isInstance` and `searchQuery` key on; this setter owns ONLY registry entries.
   */
  get categories(): string[] {
    return readVisitNoteCategoryTokens(this.resource.category)
  }

  get assessmentKinds(): string[] {
    return this.categories.filter(isAssessmentVisitNoteCategoryToken)
  }

  get disciplines(): string[] {
    return this.categories.flatMap((token) => {
      if (isAssessmentVisitNoteCategoryToken(token)) {
        return []
      }
      const code = visitNoteCategoryByToken(token)?.code
      return code ? [code] : []
    })
  }

  set categories(tokens: string[] | undefined) {
    this.resource.category = mergeVisitNoteCategoryTokens(
      this.resource.category,
      tokens
    )
  }

  get startsAt(): string {
    const startsAt = this.resource.event?.[0].period?.start
    if (!startsAt) {
      console.error(`${this.reference.reference} missing startsAt`)
    }
    return startsAt ?? new Date().toISOString()
  }

  set startsAt(startsAt) {
    this.resource.event = [{ period: { start: startsAt } }]
  }

  get signedPractitionerRefs(): {
    reference: Reference<Practitioner>
    time?: string
  }[] {
    return (
      this.resource.attester
        ?.filter((attester) => isOfType(attester.party, 'Practitioner'))
        ?.map((attester) => ({
          reference: attester.party as Reference<Practitioner>,
          time: attester.time,
        }))
        .reverse() ?? []
    )
  }

  addPractitionerSignature(practitionerRef: Reference<Practitioner>): void {
    this.author = practitionerRef
    this.addAttesterPractitioner(practitionerRef)
  }

  get signedOn(): string | undefined {
    return this.resource.attester?.[0]?.time
  }

  get signer(): Reference<Practitioner> | undefined {
    const party = this.resource.attester?.[0]?.party
    if (!party) {
      return undefined
    }
    invariant(
      isOfType(party, 'Practitioner'),
      'Assessment signer must be a Practitioner reference'
    )
    return party
  }

  get sections(): CompositionSection[] {
    return this.resource.section ?? []
  }

  set sections(sections: CompositionSection[]) {
    this.resource.section = sections
    this.date = now()
    this.resource.attester = undefined
  }

  setNthSectionText(index: number, text: string): void {
    const section = this.sections[index]
    invariant(section, `Assessment section at index ${index} must exist`)
    // No-op when the new text is semantically equivalent to the current text.
    // Without this guard, a no-op write would strip the attester and bump
    // `date` via the side effects below — silently unsigning a signed
    // assessment without any real edit. `htmlEquivalent` (not raw `===`)
    // because TipTap v3 re-serializes stored HTML on load with reordered tag
    // attributes; that byte-different but identical re-emit must not unsign.
    if (htmlEquivalent(section.text?.div ?? '', text)) {
      return
    }
    section.text = { div: text, status: 'additional' }
    /* Recomputed from the text just written, so a validated tool the clinician
     * placed in this section becomes findable from the response side and one
     * they deleted stops being. */
    const entry = withValidatedToolEntries(section.entry, text)
    section.entry = entry.length ? entry : undefined
    this.date = now()
    this.resource.attester = undefined
  }
}
